/**
 * True for a transport-level fetch FAILURE: a request that produced no usable HTTP response —
 * aborted (navigating away / reload), offline, a CORS misconfig, or a raw "Failed to fetch". This is
 * the core shared by Sentry's `beforeSend` filter (sentry.ts) and the best-effort reconcile /
 * live-update catch blocks, so the two can never drift apart.
 *
 * It matches BOTH the AppError's `errorCode` AND its stringified form, because by the time some of
 * these are caught (or re-wrapped by an intermediate layer) the property may be gone while the marker
 * still survives in the message (the toString reads `undefined/fetch.failed: …`, i.e. not a clean
 * AppError — which is why an `errorCode`-only check let these through).
 */
export const isFetchFailure = (err: any): boolean => {
    if (!err) {
        return false;
    }

    let asString = '';
    try {
        asString = String(err);
    } catch {
        // some thrown values can't be stringified; fall through to the property check
    }

    // a transient request with no usable response — matched by errorCode when the AppError is intact,
    // and by the stringified marker in case an intermediate layer re-wrapped it and dropped the prop
    if (err.errorCode === 'fetch.failed' || err.errorCode === 'fetch.missingBody') {
        return true;
    }
    if (asString.includes('fetch.failed') || asString.includes('fetch.missingBody')) {
        return true;
    }

    // a raw aborted / failed-to-fetch error from the browser (or from inside mapbox)
    if (asString === 'AbortError: Fetch is aborted' || asString.includes('Failed to fetch')) {
        return true;
    }

    // known offline: the request can't succeed and isn't actionable (guarded for non-browser envs)
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return true;
    }

    return false;
};

/**
 * True when a failed fetch is an EXPECTED, non-actionable outcome for a best-effort live-update /
 * reconcile catch block (which fire on every socket (re)connect and foreground): a transport-level
 * fetch failure (see {@link isFetchFailure}), OR an expected client-side 4xx — a private note a
 * co-owner isn't authorized to see (403), or one a co-owner deleted (404).
 *
 * The 4xx branch lives HERE rather than in the shared core because Sentry's beforeSend deliberately
 * does NOT treat every 4xx as ignorable (an unexpected 400 can be a real bug). Genuine failures (5xx,
 * unknown) return false and should still be logged.
 */
export const isExpectedFetchError = (err: any): boolean => {
    if (isFetchFailure(err)) {
        return true;
    }

    // any 4xx — an expected client-side state for a best-effort note fetch (403 private, 404 deleted)
    return Boolean(err) && typeof err.status === 'number' && err.status >= 400 && err.status < 500;
};
